import type { ApiEnvelope } from '../auth/types';
import { videosAndArticleUrl } from '../config/api';
import { postJson, RequestFailure } from './httpClient';

export type ArticleAccent = 'yellow' | 'green';

export interface ArticleItem {
  id: string;
  heading: string;
  tag: string;
  number: string;
  image: string;
  articleUrl: string;
  accent: ArticleAccent;
}

export type ArticlesErrorKind = 'network' | 'server' | 'api' | 'parse' | 'unknown';

export class ArticlesRequestError extends Error {
  readonly kind: ArticlesErrorKind;

  constructor(kind: ArticlesErrorKind, message: string) {
    super(message);
    this.name = 'ArticlesRequestError';
    this.kind = kind;
  }
}

interface RawArticleDoc {
  _id?: string | { $oid: string };
  heading?: string;
  tag?: string;
  number?: string | number;
  image?: string;
  article_url?: string;
}

const ACCENTS: ArticleAccent[] = ['yellow', 'green'];

function resolveId(raw: RawArticleDoc['_id']): string {
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object' && '$oid' in raw) return raw.$oid;
  return '';
}

function toArticleItem(doc: RawArticleDoc, index: number): ArticleItem | null {
  const heading = doc.heading?.trim();
  const articleUrl = doc.article_url?.trim();
  if (!heading || !articleUrl) {
    return null;
  }

  const numberValue = doc.number;
  const number =
    numberValue !== undefined && numberValue !== null && String(numberValue).trim()
      ? String(numberValue).trim()
      : String(index + 1);

  return {
    id: resolveId(doc._id) || articleUrl,
    heading,
    tag: doc.tag?.trim() || '',
    number,
    image: doc.image?.trim() || '',
    articleUrl,
    accent: ACCENTS[index % ACCENTS.length],
  };
}

function toArticlesError(error: unknown): ArticlesRequestError {
  if (error instanceof RequestFailure) {
    const kind =
      error.kind === 'server'
        ? 'server'
        : error.kind === 'parse'
          ? 'parse'
          : 'network';
    return new ArticlesRequestError(kind, error.message);
  }
  if (error instanceof ArticlesRequestError) {
    return error;
  }
  if (error instanceof Error) {
    return new ArticlesRequestError('unknown', error.message);
  }
  return new ArticlesRequestError('unknown', 'Something went wrong while loading articles.');
}

export async function fetchArticles(signal?: AbortSignal): Promise<ArticleItem[]> {
  try {
    const envelope = (await postJson(
      videosAndArticleUrl(),
      { type: 'ARTICLE' },
      signal
    )) as unknown as ApiEnvelope<RawArticleDoc[]>;

    if (envelope.result === 0) {
      throw new ArticlesRequestError(
        'api',
        envelope.message || 'No articles are available right now.'
      );
    }

    const docs = Array.isArray(envelope.response) ? envelope.response : [];
    return docs
      .map((doc, index) => toArticleItem(doc, index))
      .filter((item): item is ArticleItem => item !== null);
  } catch (error) {
    if (error instanceof ArticlesRequestError) {
      throw error;
    }
    throw toArticlesError(error);
  }
}

export function articlesErrorTitle(kind: ArticlesErrorKind): string {
  switch (kind) {
    case 'network':
      return 'No connection';
    case 'server':
      return 'Server unavailable';
    case 'parse':
      return 'Unexpected response';
    case 'api':
      return 'Could not load articles';
    default:
      return 'Something went wrong';
  }
}
